import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, GraduationCap, PlayCircle, ListChecks, Award, ArrowUpRight } from 'lucide-react';
import { Button, Container, Eyebrow, Reveal } from './ui';
import { INK_U_URL, STATS, orderLink } from '@/lib/site';

/** Interior page hero: breadcrumb, headline, intro, actions, optional visual. */
export function PageHero({
  crumbs,
  eyebrow,
  title,
  intro,
  actions,
  visual,
  dark = false,
}: {
  crumbs?: Array<{ label: string; to?: string }>;
  eyebrow?: string;
  title: ReactNode;
  intro?: ReactNode;
  actions?: ReactNode;
  visual?: ReactNode;
  dark?: boolean;
}) {
  return (
    <section className={`relative overflow-hidden ${dark ? 'bg-navy-900 text-white' : 'bg-white border-b border-glass-border'}`}>
      <div className={`absolute inset-0 ${dark ? 'bg-grid-dark' : 'bg-grid-light'} [mask-image:linear-gradient(to_bottom,black,transparent)]`} />
      <div className={`absolute -top-32 right-0 w-[520px] h-[520px] rounded-full blur-[120px] ${dark ? 'bg-brand-secondary/25' : 'bg-brand-secondary/8'}`} />
      <div className={`absolute -bottom-40 -left-20 w-[480px] h-[480px] rounded-full blur-[120px] ${dark ? 'bg-brand-primary/25' : 'bg-brand-primary/8'}`} />
      <Container className="relative py-14 lg:py-20">
        {crumbs && (
          <nav aria-label="Breadcrumb" className={`mb-6 flex flex-wrap items-center gap-1 text-xs ${dark ? 'text-slate-400' : 'text-text-muted'}`}>
            <Link to="/" className={dark ? 'hover:text-white' : 'hover:text-text-primary'}>
              Home
            </Link>
            {crumbs.map((c) => (
              <span key={c.label} className="inline-flex items-center gap-1">
                <ChevronRight size={12} />
                {c.to ? (
                  <Link to={c.to} className={dark ? 'hover:text-white' : 'hover:text-text-primary'}>
                    {c.label}
                  </Link>
                ) : (
                  <span className={dark ? 'text-slate-200' : 'text-text-secondary'}>{c.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <div className={`grid gap-12 items-center ${visual ? 'lg:grid-cols-[1.05fr_1fr]' : ''}`}>
          <div className={visual ? '' : 'max-w-3xl'}>
            {eyebrow && (
              <div className="mb-5">
                <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
              </div>
            )}
            <h1 className={`text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08] ${dark ? 'text-white' : 'text-text-primary'}`}>{title}</h1>
            {intro && <p className={`mt-6 text-lg leading-relaxed max-w-2xl ${dark ? 'text-slate-300' : 'text-text-secondary'}`}>{intro}</p>}
            {actions && <div className="mt-8 flex flex-wrap gap-3">{actions}</div>}
          </div>
          {visual && <div className="relative">{visual}</div>}
        </div>
      </Container>
    </section>
  );
}

/** The dark closing band on every marketing page. Leads to the order forms. */
export function CtaBand({
  title = 'Ready to see it on your floor?',
  body = 'Tell us about your machines and we will put together a quote — appliance, software, cloud services and training — usually within one business day.',
}: {
  title?: string;
  body?: string;
}) {
  return (
    <section className="relative bg-navy-900 overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark" />
      <div className="absolute -top-24 left-1/3 w-[600px] h-[400px] rounded-full bg-brand-primary/25 blur-[140px]" />
      <div className="absolute -bottom-24 right-0 w-[500px] h-[400px] rounded-full bg-brand-secondary/25 blur-[140px]" />
      <Container className="relative py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">{title}</h2>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl leading-relaxed">{body}</p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 lg:justify-end">
            <Button to={orderLink('quote')} size="lg" variant="light" arrow>
              Request a quote
            </Button>
            <Button to={orderLink('demo')} size="lg" variant="outline-light">
              Book a live demo
            </Button>
          </div>
        </div>
      </Container>
    </section>
  );
}

/** A prominent promotion of Ink U — used on the home, platform and cloud pages. */
export function TrainingBand() {
  const points = [
    { icon: PlayCircle, title: `${STATS.videos} videos`, body: `${STATS.courses} courses from first login to MES` },
    { icon: ListChecks, title: 'Graded quizzes', body: 'A quiz after every section, unlimited retakes' },
    { icon: Award, title: 'Certification', body: 'Associate and Professional credentials' },
  ];
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#eef2ff] via-white to-[#f5f3ff] border-y border-glass-border">
      <Container className="relative py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>Ink U Training Portal</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">
              Your team, certified on Scada.Ink — <span className="text-brand-secondary">free</span>
            </h2>
            <p className="mt-4 text-lg text-text-secondary leading-relaxed">
              Ink U is a complete training portal: step-by-step videos with transcripts and notes, section quizzes that track progress,
              and a two-level certification program that proves your engineers can deliver.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {points.map((p) => (
                <div key={p.title} className="glass-card p-4">
                  <p.icon size={20} className="text-brand-secondary" />
                  <div className="mt-2 text-sm font-semibold text-text-primary">{p.title}</div>
                  <div className="text-xs text-text-secondary mt-1 leading-snug">{p.body}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button href={INK_U_URL} size="lg">
                <GraduationCap size={16} /> Start free on Ink U <ArrowUpRight size={15} />
              </Button>
              <Button to="/training/certification" size="lg" variant="secondary" arrow>
                Certification program
              </Button>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <CoursePlayerMockup />
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function CoursePlayerMockup() {
  const lessons = [
    { n: '7.1', t: 'What Is a Dashboard?', done: true },
    { n: '7.2', t: 'Creating Your First Dashboard', done: true },
    { n: '7.3', t: 'Binding Widgets to Live Tags', current: true },
    { n: '7.4', t: 'Gauges, Trends & Tables' },
    { n: '7.5', t: 'Section Quiz' , quiz: true },
  ];
  return (
    <div className="relative">
      <div className="glass-panel overflow-hidden shadow-xl shadow-brand-secondary/10">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-glass-border bg-white">
          <span className="text-xs font-semibold text-text-primary">Ink U · Course 7 — Dashboards</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-status-running/10 text-status-running font-semibold">64% complete</span>
        </div>
        <div className="grid sm:grid-cols-[1.5fr_1fr]">
          <div className="relative aspect-video bg-navy-900 flex items-center justify-center">
            <div className="absolute inset-4 rounded-lg border border-white/10 bg-grid-dark" />
            <div className="absolute left-6 top-6 right-6 grid grid-cols-3 gap-2 opacity-70">
              {[72, 45, 88].map((v) => (
                <div key={v} className="h-10 rounded bg-white/10 flex items-end p-1">
                  <div className="w-full rounded-sm bg-brand-accent/70" style={{ height: `${v}%` }} />
                </div>
              ))}
            </div>
            <span className="relative w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
              <span className="ml-1 w-0 h-0 border-y-[9px] border-y-transparent border-l-[14px] border-l-brand-secondary" />
            </span>
            <div className="absolute bottom-3 left-4 right-4">
              <div className="h-1 rounded-full bg-white/20">
                <div className="h-1 rounded-full bg-brand-accent w-[38%]" />
              </div>
              <div className="mt-1.5 flex justify-between text-[10px] text-slate-300">
                <span>1:43</span>
                <span>4:30</span>
              </div>
            </div>
          </div>
          <div className="p-3 space-y-1 bg-white">
            {lessons.map((l) => (
              <div key={l.n} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] ${l.current ? 'bg-brand-primary/8 text-brand-primary font-semibold' : 'text-text-secondary'}`}>
                <span
                  className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] shrink-0 ${
                    l.done ? 'bg-status-running text-white' : l.quiz ? 'bg-brand-secondary/15 text-brand-secondary' : 'border border-glass-border-bright'
                  }`}
                >
                  {l.done ? '✓' : l.quiz ? '?' : ''}
                </span>
                <span className="truncate">
                  {l.n} {l.t}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute -bottom-6 -left-4 sm:-left-8 glass-panel px-4 py-3 flex items-center gap-3 shadow-lg animate-float-slow">
        <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#fbbf24] to-[#ca8a04] text-white flex items-center justify-center">
          <Award size={20} />
        </span>
        <div>
          <div className="text-xs font-bold text-text-primary">Certified Associate</div>
          <div className="text-[10px] text-text-muted">Scada.Ink · SCA</div>
        </div>
      </div>
    </div>
  );
}
